import { Box } from '@mui/material';

/// Arco-íris em volta do botão da IA: quadrado com gradiente cônico girando, só a borda aparece.
/// O miolo é recortado por máscara, não tapado com cor, para servir em qualquer fundo.

/// Matizes puros e saturados: cor lavada some sob o desfoque do brilho.
const RAINBOW_CONIC =
    'conic-gradient(#ff0055, #ff7a00, #ffd600, #22ff88, #00e5ff, #6b5bff, #ff3df0, #ff0055)';

const RING_BAND = 1.5;

/// Uma volta. Mais rápido que isso vira pisca-pisca num canto fixo da tela.
const SPIN = '5s';

/// Cobre a diagonal do botão em qualquer ângulo; proporcional à largura.
const SPIN_SIZE = '150%';

/// Recorta o miolo: mostra só o que fica no padding, que é a faixa do anel.
const RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)';

/// Brilho delimitado por máscara radial: overflow + borderRadius ainda deixa ver um retângulo.
const GLOW_MASK = 'radial-gradient(ellipse at center, #000 22%, transparent 70%)';

const SPINNER = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: SPIN_SIZE,
    aspectRatio: '1 / 1',
    background: RAINBOW_CONIC,
    transform: 'translate(-50%, -50%)',
    animation: `aiAuraSpin ${SPIN} linear infinite`,
} as const;

interface Props {
    children: React.ReactNode;
    radius: number;
    darkMode: boolean;
}

export const AiAura = ({ children, radius, darkMode }: Props) => (
    <Box
        sx={{
            position: 'relative',
            // Sem isto o zIndex -1 do brilho o joga atrás do fundo da tela.
            isolation: 'isolate',
            // O translate é parte do transform animado; sem ele nos keyframes o quadrado sai do centro.
            '@keyframes aiAuraSpin': {
                from: { transform: 'translate(-50%, -50%) rotate(0turn)' },
                to: { transform: 'translate(-50%, -50%) rotate(1turn)' },
            },
            '&:hover .ai-aura-glow': { opacity: darkMode ? 0.6 : 0.45 },
        }}
    >
        <Box
            className="ai-aura-glow"
            aria-hidden="true"
            sx={{
                position: 'absolute',
                inset: '-14px',
                pointerEvents: 'none',
                zIndex: -1,
                opacity: darkMode ? 0.4 : 0.28,
                transition: 'opacity 220ms ease',
                filter: 'blur(12px)',
                maskImage: GLOW_MASK,
                WebkitMaskImage: GLOW_MASK,
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskSize: '100% 100%',
                WebkitMaskSize: '100% 100%',
            }}
        >
            <Box sx={SPINNER} />
        </Box>

        <Box
            aria-hidden="true"
            sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: `${radius}px`,
                padding: `${RING_BAND}px`,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 1,
                mask: RING_MASK,
                WebkitMask: RING_MASK,
                maskComposite: 'exclude',
                WebkitMaskComposite: 'xor',
            }}
        >
            <Box sx={SPINNER} />
        </Box>

        {children}
    </Box>
);
